import { getUTMFromCookies } from '~/services/_utils/utmTracking'

const NETHUNT_USER_WEBHOOK_URL = 'https://nethunt.com/service/automation/hooks/68bef96e855501d2d02d542e'
const NETHUNT_ORGANIZATION_WEBHOOK_URL = 'https://nethunt.com/service/automation/hooks/68c3f1b94bb6fc7e31cbce8c'
const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbz2ghXkXN68Kj51_bTCVKmCjP3SgRKQtlhSiArS_CqF85_dkULZd0wClbwV9hTFsVBeKg/exec'

const FIELD_MAP = {
  0: 'document_volume',
  1: 'service_usage',
  2: 'first_action_choice'
}

const VALUE_MAP = {
  0: { 0: '10', 1: '10-100', 2: '100+' },
  1: { 0: '1', 1: '2-10', 2: '10+' },
  2: { 0: 'create_doc', 1: 'template', 2: 'create_org' }
}

const ACTION_MAP = {
  create_document: 'create_doc',
  choose_template: 'template',
  create_organization: 'create_org'
}

function processOnboardingData(selectedAnswers, steps, action = null) {
  const onboardingData = {}
  Object.keys(selectedAnswers).forEach(key => {
    if (key === 'firstAction') return
    const stepIndex = steps.findIndex(s => s.id === key)
    if (stepIndex === -1) return
    const step = steps[stepIndex]
    const optionIndex = step.options.findIndex(opt => opt.value === selectedAnswers[key])
    if (optionIndex === -1) return
    const fieldName = FIELD_MAP[stepIndex]
    const answerValue = VALUE_MAP[stepIndex]?.[optionIndex]
    if (fieldName && answerValue) {
      onboardingData[fieldName] = answerValue
    }
  })
  if (action) {
    onboardingData.first_action = ACTION_MAP[action] || action
  }
  return onboardingData
}

function createGoogleSheetsFormData(userData, completionType, onboardingData, utm) {
  const data = new FormData()
  data.append('email', userData.email || '')
  data.append('created_at', new Date().toLocaleString('uk-UA', { timeZone: 'Europe/Kiev' }))
  data.append('completion_type', completionType)
  data.append('registered_at', userData.registeredAt || new Date().toISOString())
  data.append('document_volume', onboardingData.document_volume || '')
  data.append('service_usage', onboardingData.service_usage || '')
  data.append('first_action_choice', onboardingData.first_action_choice || '')
  data.append('first_action', onboardingData.first_action || '')
  data.append('onboarding_completed', completionType === 'completed' ? 'yes' : 'no')
  data.append('utm_source', utm.utm_source || 'direct')
  data.append('utm_medium', utm.utm_medium || 'none')
  data.append('utm_campaign', utm.utm_campaign || '')
  return data
}

async function sendToGoogleSheets(userData, completionType, onboardingData, utm) {
  try {
    const data = createGoogleSheetsFormData(userData, completionType, onboardingData, utm)
    const response = await fetch(GOOGLE_SHEETS_URL, { method: 'POST', body: data })
    const result = await response.json()
    return result?.status === 'success'
  } catch (error) {
    return false
  }
}

export function sendOnboardingExitToGoogleSheets(userData, selectedAnswers = {}, steps = []) {
  if (navigator.sendBeacon && !window._onboardingExitSent) {
    window._onboardingExitSent = true
    const onboardingData = processOnboardingData(selectedAnswers, steps)
    const utm = getUTMFromCookies()
    const formData = createGoogleSheetsFormData(userData, 'tab_closed', onboardingData, utm)
    navigator.sendBeacon(GOOGLE_SHEETS_URL, formData)
  }
}

export async function sendUserRegistrationToNetHunt(userData, completionType, selectedAnswers = {}, steps = [], action = null, referralLink = null) {
  const onboardingData = processOnboardingData(selectedAnswers, steps, action)
  const utm = getUTMFromCookies()
  const shouldSkipNetHunt = onboardingData.service_usage === '1'

  const netHuntPayload = {
    event: 'user_registration',
    timestamp: new Date().toISOString(),
    user: {
      email: userData.email,
      registeredAt: userData.registeredAt || new Date().toISOString()
    },
    source: 'dubidoc_frontend',
    referralLink: referralLink || null,
    ...onboardingData,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign
  }

  try {
    const [netHuntResult, googleSheetsResult] = await Promise.allSettled([
      shouldSkipNetHunt ? Promise.resolve({ status: 'skipped' }) :
        fetch(NETHUNT_USER_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(netHuntPayload)
        }),
      sendToGoogleSheets(userData, completionType, onboardingData, utm)
    ])

    return {
      netHunt: shouldSkipNetHunt ? null : (netHuntResult.status === 'fulfilled'),
      googleSheets: googleSheetsResult.status === 'fulfilled' && googleSheetsResult.value
    }
  } catch (error) {
    return { netHunt: false, googleSheets: false }
  }
}

export async function sendOrganizationRegistrationToNetHunt(organizationData, userData) {
  try {
    const utm = getUTMFromCookies()
    const payload = {
      event: 'organization_registration',
      org_id: organizationData.id,
      org_name: organizationData.name,
      edrpou: organizationData.edrpou,
      id: userData.id,
      email: userData.email,
      source: 'dubidoc_frontend',
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign
    }

    await fetch(NETHUNT_ORGANIZATION_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    return true
  } catch (error) {
    return false
  }
}
